import authService, { TOKEN_TYPES } from "./authService";

const IAM_URL = import.meta.env.VITE_IAM_URL;
const MANAGE_BASE_URL = `${IAM_URL}/manage`;

export type ManagedGrantDto = {
  trial: string;
  role: string;
  date_assigned: string | null;
};

export type ManagedUserInvitedByDto = {
  id: string;
  name: string;
  email: string;
};

export type ManagedUserDto = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  invited_by: ManagedUserInvitedByDto | null;
  invited_on: string | null;
  grants: ManagedGrantDto[];
};

export type ManagedTrialDto = {
  id: string;
  name: string;
  description: string;
  phase: string;
  sponsor: string;
  is_active: boolean;
  // Role slugs available for this trial (e.g. "full_access"). Trials with no
  // explicitly assigned roles receive every role slug from the backend.
  roles: string[];
};

export type CreateManagedUserPayload = {
  email: string;
  first_name?: string;
  last_name?: string;
  role_assignment: string;
  trials?: string[];
  client_admin?: boolean;
};

export type UpdateManagedUserPayload =
  | {
      role_assignment: string;
      trials: string[];
      client_admin?: boolean;
      first_name?: string;
      last_name?: string;
    }
  | {
      // "admin" shorthand revokes all environment trial grants and applies
      // client_admin in a single PATCH.
      role_assignment: "admin";
      client_admin?: boolean;
      first_name?: string;
      last_name?: string;
    }
  | {
      // Name-only update: no role_assignment, so the backend leaves grants
      // and client_admin untouched.
      first_name?: string;
      last_name?: string;
    };

export type DeleteManagedUserOptions = {
  trial?: string;
};

export class AdminManageApiError extends Error {
  status: number;
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "AdminManageApiError";
    this.status = status;
    this.body = body;
  }
}

async function parseResponseBody(response: Response) {
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text || null;
}

async function manageRequest<T>(path = "", init: RequestInit = {}): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS);

  if (!token) {
    throw new AdminManageApiError("No active session", 401);
  }

  const response = await fetch(`${MANAGE_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  const body = await parseResponseBody(response);

  if (!response.ok) {
    const message =
      (typeof body === "object" && body && "detail" in body && body.detail) ||
      (typeof body === "object" && body && "message" in body && body.message) ||
      (typeof body === "string" && body) ||
      `Request failed with status ${response.status}`;

    throw new AdminManageApiError(String(message), response.status, body);
  }

  return body as T;
}

function buildTrialQuery(options: DeleteManagedUserOptions = {}) {
  if (!options.trial) {
    return "";
  }

  return `?trial=${encodeURIComponent(options.trial)}`;
}

export const adminManageService = {
  listManagedUsers: async ({
    trial,
  }: {
    trial?: string;
  } = {}): Promise<ManagedUserDto[]> => {
    const query = trial ? `?trial=${encodeURIComponent(trial)}` : "";
    return manageRequest<ManagedUserDto[]>(`/users${query}`, { method: "GET" });
  },

  listManagedTrials: async (): Promise<ManagedTrialDto[]> => {
    return manageRequest<ManagedTrialDto[]>("/trials", { method: "GET" });
  },

  getManagedUser: async (userId: string): Promise<ManagedUserDto> => {
    return manageRequest<ManagedUserDto>(`/users/${userId}`, {
      method: "GET",
    });
  },

  createManagedUser: async (
    payload: CreateManagedUserPayload,
  ): Promise<ManagedUserDto> => {
    return manageRequest<ManagedUserDto>("/users", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateManagedUser: async (
    userId: string,
    payload: UpdateManagedUserPayload,
  ): Promise<ManagedUserDto> => {
    return manageRequest<ManagedUserDto>(`/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  deleteManagedUser: async (
    userId: string,
    options: DeleteManagedUserOptions = {},
  ): Promise<void> => {
    await manageRequest<null>(`/users/${userId}${buildTrialQuery(options)}`, {
      method: "DELETE",
    });
  },
};
